import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, fmtDate, fmtDateTime, paymentModeLabel, orderPieces, orderWeight } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Truck, CheckCircle2, History, Receipt as ReceiptIcon, CalendarClock, XCircle, Cog, RotateCcw, Wallet, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Receipt from "@/components/Receipt";
import { useAuth } from "@/context/AuthContext";
import OrderAuditLog from "@/components/OrderAuditLog";

export default function OrderDetailDialog({ orderId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couriers, setCouriers] = useState([]);
  const [courierId, setCourierId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [reason, setReason] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [payAmt, setPayAmt] = useState(0);
  const [payMode, setPayMode] = useState("");
  const [re, setRe] = useState({ collect_date: "", collect_time: "", delivery_date: "", delivery_time: "" });
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [eItems, setEItems] = useState([]);
  const [eDiscount, setEDiscount] = useState(0);
  const [eWaive, setEWaive] = useState(false);

  const load = () => {
    if (!orderId) return;
    setLoading(true);
    api.get(`/orders/${orderId}`).then((r) => {
      const o = r.data;
      setOrder(o);
      setCourierId(o.courier_id || "");
      setDeliveryDate(o.delivery_date || "");
      setDeliveryTime(o.delivery_time || "");
      setEditItems((o.items || []).map((it) => ({ ...it, _uid: crypto.randomUUID() })));
      setRe({
        collect_date: o.collect_date || "", collect_time: o.collect_time || "",
        delivery_date: o.delivery_date || "", delivery_time: o.delivery_time || "",
      });
      setShowReschedule(false);
      setReason("");
      setShowPay(false);
      setPayAmt(o.amount_paid ?? 0);
      setPayMode(o.payment_mode || "");
      setEditOpen(false);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && orderId) {
      load();
      api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {});
      api.get("/services?active_only=true").then((r) => setServices(r.data)).catch(() => {});
      api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [open, orderId]); // eslint-disable-line

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      load();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const updEdit = (uid, patch) => setEditItems((x) => x.map((it) => (it._uid === uid ? { ...it, ...patch } : it)));
  const editSub = (it) => it.billing_mode === "kilo" ? (Number(it.weight) || 0) * (Number(it.unit_price) || 0) : (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
  const editPieces = editItems.reduce((a, it) => a + (Number(it.quantity) || 0), 0);

  const markProcessed = () => act(async () => {
    const items = editItems.map((it) => ({
      service_id: it.service_id || null, name: it.name, billing_mode: it.billing_mode,
      unit_price: Number(it.unit_price) || 0, quantity: Number(it.quantity) || 0, weight: Number(it.weight) || 0,
    }));
    await api.post(`/orders/${orderId}/process`, { items });
    toast.success("Commande mise en traitement");
  });

  const markReady = () => act(async () => {
    await api.post(`/orders/${orderId}/ready`, { delivery_date: deliveryDate || null, delivery_time: deliveryTime || null });
    toast.success("Commande marquée prête (Traité)");
  });

  const assign = () => act(async () => {
    if (!courierId) { toast.error("Choisissez un livreur"); return; }
    await api.post(`/orders/${orderId}/assign`, { courier_id: courierId, delivery_date: deliveryDate || null, delivery_time: deliveryTime || null });
    toast.success("Livreur attribué");
  });

  const planDelivery = () => act(async () => {
    if (!deliveryDate || !deliveryTime) { toast.error("Date et heure de livraison requises"); return; }
    await api.post(`/orders/${orderId}/reschedule`, { delivery_date: deliveryDate, delivery_time: deliveryTime });
    toast.success("Livraison planifiée");
  });

  const cancelOrder = () => act(async () => {
    await api.post(`/orders/${orderId}/cancel`, { reason: reason || null });
    toast.success("Commande annulée");
  });

  const restoreOrder = () => act(async () => {
    await api.post(`/orders/${orderId}/restore`);
    toast.success("Commande restaurée");
  });

  const correctPayment = () => act(async () => {
    if (!payMode) { toast.error("Choisissez un mode de paiement"); return; }
    await api.post(`/orders/${orderId}/correct-payment`, { amount_paid: Number(payAmt) || 0, payment_mode: payMode });
    toast.success("Paiement corrigé");
  });

  const doReschedule = () => act(async () => {
    await api.post(`/orders/${orderId}/reschedule`, re);
    toast.success("Programmation mise à jour");
  });

  const openEdit = () => {
    setEItems((order.items || []).map((it) => ({ ...it, _uid: crypto.randomUUID() })));
    setEDiscount(order.discount || 0);
    setEWaive(!!order.delivery_waived);
    setEditOpen(true);
  };
  const addEItem = () => setEItems((x) => [...x, { _uid: crypto.randomUUID(), service_id: "", name: "", billing_mode: "piece", unit_price: 0, quantity: 1, weight: 0 }]);
  const removeEItem = (uid) => setEItems((x) => x.filter((it) => it._uid !== uid));
  const updEItem = (uid, patch) => setEItems((x) => x.map((it) => (it._uid === uid ? { ...it, ...patch } : it)));
  const pickEService = (uid, sid) => {
    const svc = services.find((s) => s.id === sid);
    if (svc) updEItem(uid, { service_id: svc.id, name: svc.name, billing_mode: svc.billing_mode, unit_price: svc.unit_price });
  };
  const saveEdit = () => act(async () => {
    const items = eItems.map((it) => ({
      service_id: it.service_id || null, name: it.name, billing_mode: it.billing_mode,
      unit_price: Number(it.unit_price) || 0, quantity: Number(it.quantity) || 0, weight: Number(it.weight) || 0,
    }));
    await api.put(`/orders/${orderId}`, { items, discount: Number(eDiscount) || 0, waive_delivery: eWaive });
    setEditOpen(false);
    toast.success("Commande mise à jour");
  });

  const viewReceipt = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/receipt`);
      setReceipt(data);
    } catch (e) {
      toast.error("Aucun reçu disponible");
    }
  };

  const st = order?.status;
  const isActive = st && !["Livré", "Annulée"].includes(st);
  const scheduled = !!(order?.delivery_date && order?.delivery_time);
  const eItemSub = (it) => (it.billing_mode === "kilo" ? (Number(it.weight) || 0) : (Number(it.quantity) || 0)) * (Number(it.unit_price) || 0);
  const eServiceTotal = eItems.reduce((a, it) => a + eItemSub(it), 0);
  const eBaseFee = order?.base_delivery_fee || 0;
  const eThreshold = settings.free_delivery_threshold || 0;
  const eEffFee = eWaive ? 0 : (eThreshold && eServiceTotal >= eThreshold ? 0 : eBaseFee);
  const eTotal = Math.max(eServiceTotal + eEffFee - (Number(eDiscount) || 0), 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {loading || !order ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 font-heading">
                  <span className="font-mono">{order.order_number}</span>
                  <StatusBadge status={order.status} />
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="bg-slate-50 rounded-xl p-4 space-y-1">
                    <div className="font-semibold text-slate-700 mb-1">Client</div>
                    <div>{order.client_name}</div>
                    <div className="text-slate-500">{order.whatsapp}</div>
                    <div className="text-slate-500">{order.address}</div>
                    <div className="text-slate-500">{order.commune_name} {order.quartier ? `· ${order.quartier}` : ""}</div>
                    <div className="text-slate-500">Réf : {order.reference || "—"}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-1">
                    <div className="font-semibold text-slate-700 mb-1">Logistique</div>
                    <div>Créée : {fmtDateTime(order.created_at)}</div>
                    <div>Collecte : {fmtDate(order.collect_date)} {order.collect_time}</div>
                    <div>Livraison prévue : {fmtDate(order.delivery_date)} {order.delivery_time || ""}</div>
                    {order.processing_at && <div>En traitement depuis : {fmtDateTime(order.processing_at)}</div>}
                    <div>Livreur : {order.courier_name || "—"}</div>
                    {order.status === "Annulée" && <div className="text-rose-600">Motif : {order.cancel_reason || "—"}</div>}
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-slate-700 mb-2 text-sm">Services</div>
                  <div className="border rounded-xl overflow-hidden">
                    {(order.items || []).length === 0 && <div className="p-3 text-sm text-slate-400 text-center">Services non renseignés</div>}
                    {(order.items || []).map((it, i) => (
                      <div key={`${it.service_id || it.name}-${i}`} className="flex justify-between px-3 py-2 text-sm border-b last:border-0">
                        <span>{it.name} <span className="text-slate-400">{it.billing_mode === "kilo" ? `· ${it.weight}kg` : ""}{it.quantity ? ` · x${it.quantity}` : ""} @ {money(it.unit_price)}</span></span>
                        <b>{money(it.subtotal)}</b>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-brand-50 rounded-xl p-4 text-sm space-y-1">
                  <div className="flex justify-between"><span>Services</span><b>{money(order.service_total)}</b></div>
                  <div className="flex justify-between"><span>Nombre de pièces</span><b data-testid="od-total-pieces">{orderPieces(order)}</b></div>
                  <div className="flex justify-between"><span>Poids total</span><b>{orderWeight(order)} kg</b></div>
                  <div className="flex justify-between"><span>Livraison</span><b>{money(order.delivery_fee)}</b></div>
                  <div className="flex justify-between"><span>Remise</span><b>-{money(order.discount)}</b></div>
                  <div className="flex justify-between text-base border-t border-brand-200 pt-1 mt-1"><span className="font-semibold">Total</span><b className="text-brand-700">{money(order.total)}</b></div>
                  <div className="flex justify-between"><span>Payé</span><b>{money(order.amount_paid)}</b></div>
                  <div className="flex justify-between"><span>Solde</span><b className={order.balance > 0 ? "text-amber-600" : "text-emerald-600"}>{money(order.balance)}</b></div>
                  {order.payment_mode && <div className="text-slate-500">Mode : {paymentModeLabel[order.payment_mode]}</div>}
                </div>

                {/* ---- Édition de la commande (Super Admin, commande active) ---- */}
                {isSuperAdmin && isActive && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <button data-testid="od-edit-toggle" onClick={() => (editOpen ? setEditOpen(false) : openEdit())} className="font-semibold text-slate-700 text-sm flex items-center gap-2">
                      <Pencil className="w-4 h-4" /> Modifier la commande (services, remise, livraison)
                    </button>
                    {editOpen && (
                      <>
                        <div className="space-y-2">
                          {eItems.map((it) => (
                            <div key={it._uid} className="grid grid-cols-12 gap-2 items-end bg-slate-50 rounded-xl p-2">
                              <div className="col-span-4">
                                <Label className="text-xs">Service</Label>
                                <Select value={it.service_id || ""} onValueChange={(v) => pickEService(it._uid, v)}>
                                  <SelectTrigger data-testid={`od-edit-service-${it._uid}`} className="mt-1 h-9"><SelectValue placeholder="Service" /></SelectTrigger>
                                  <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.billing_mode === "kilo" ? "kg" : "pièce"})</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-2"><Label className="text-xs">PU</Label><Input className="mt-1 h-9" type="number" value={it.unit_price} onChange={(e) => updEItem(it._uid, { unit_price: e.target.value })} /></div>
                              {it.billing_mode === "kilo" ? (
                                <div className="col-span-2"><Label className="text-xs">Poids kg</Label><Input data-testid={`od-edit-weight-${it._uid}`} className="mt-1 h-9" type="number" value={it.weight} onChange={(e) => updEItem(it._uid, { weight: e.target.value })} /></div>
                              ) : (
                                <div className="col-span-2"><Label className="text-xs">Qté</Label><Input data-testid={`od-edit-qty-${it._uid}`} className="mt-1 h-9" type="number" value={it.quantity} onChange={(e) => updEItem(it._uid, { quantity: e.target.value })} /></div>
                              )}
                              <div className="col-span-3 text-right text-sm font-semibold pb-2">{money(eItemSub(it))}</div>
                              <div className="col-span-1 pb-1"><button data-testid={`od-edit-remove-${it._uid}`} onClick={() => removeEItem(it._uid)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button></div>
                            </div>
                          ))}
                        </div>
                        <Button data-testid="od-edit-add-item" size="sm" variant="outline" onClick={addEItem}><Plus className="w-4 h-4 mr-1" /> Ajouter un service</Button>

                        <div className="grid sm:grid-cols-2 gap-3 pt-1">
                          <div><Label className="text-xs">Remise ($)</Label><Input data-testid="od-edit-discount" type="number" value={eDiscount} onChange={(e) => setEDiscount(e.target.value)} className="mt-1" /></div>
                          <div className="flex items-center justify-between bg-slate-50 border rounded-xl px-3 py-2 mt-1 sm:mt-6">
                            <Label className="text-xs">Dispenser les frais de livraison</Label>
                            <Switch data-testid="od-edit-waive" checked={eWaive} onCheckedChange={setEWaive} />
                          </div>
                        </div>

                        <div className="bg-brand-50 rounded-xl p-3 text-sm space-y-1">
                          <div className="flex justify-between"><span>Services</span><b>{money(eServiceTotal)}</b></div>
                          <div className="flex justify-between"><span>Livraison</span><b>{money(eEffFee)}</b>{eWaive && <span className="text-emerald-600 text-xs ml-1">(dispensé)</span>}</div>
                          <div className="flex justify-between"><span>Remise</span><b>-{money(Number(eDiscount) || 0)}</b></div>
                          <div className="flex justify-between text-base border-t border-brand-200 pt-1 mt-1"><span className="font-semibold">Nouveau total</span><b data-testid="od-edit-total" className="text-brand-700">{money(eTotal)}</b></div>
                          {order.amount_paid > 0 && <p className="text-xs text-slate-400">Déjà payé {money(order.amount_paid)} — le solde sera recalculé.</p>}
                        </div>
                        <Button data-testid="od-edit-save" onClick={saveEdit} disabled={busy} className="bg-brand-600 hover:bg-brand-700">
                          <Pencil className="w-4 h-4 mr-2" /> Enregistrer les modifications
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* ---- Correction de paiement (Super Admin) ---- */}
                {isSuperAdmin && st !== "Annulée" && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <button data-testid="od-correct-pay-toggle" onClick={() => setShowPay((v) => !v)} className="font-semibold text-slate-700 text-sm flex items-center gap-2">
                      <Wallet className="w-4 h-4" /> Corriger le paiement
                    </button>
                    {showPay && (
                      <>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div><Label className="text-xs">Montant payé</Label><Input data-testid="od-correct-amount" type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1" /></div>
                          <div>
                            <Label className="text-xs">Mode de paiement</Label>
                            <Select value={payMode} onValueChange={setPayMode}>
                              <SelectTrigger data-testid="od-correct-mode" className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="bank">Banque</SelectItem>
                                <SelectItem value="mobile_money">Mobile Money</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400">Le solde et la trésorerie seront recalculés (max : {money(order.total)}).</p>
                        <Button data-testid="od-correct-pay-save" onClick={correctPayment} disabled={busy} variant="outline">
                          <Wallet className="w-4 h-4 mr-2" /> Enregistrer le paiement
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* ---- Collecté: vérification & mise en traitement (Super Admin) ---- */}
                {st === "Collecté" && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="font-semibold text-slate-700 text-sm flex items-center gap-2"><Cog className="w-4 h-4" /> Vérification avant traitement</div>
                    {isSuperAdmin ? (
                      <>
                        <div className="space-y-2">
                          {editItems.map((it) => (
                            <div key={it._uid} className="bg-slate-50 rounded-xl p-2 grid grid-cols-12 gap-2 items-end">
                              <div className="col-span-5 text-sm truncate pb-2">{it.name} <span className="text-slate-400 text-xs">({it.billing_mode === "kilo" ? "kg" : "pièce"})</span></div>
                              <div className="col-span-3"><Label className="text-xs">Poids kg</Label><Input data-testid={`od-item-weight-${it._uid}`} className="h-9 mt-1" type="number" value={it.weight} onChange={(e) => updEdit(it._uid, { weight: e.target.value })} /></div>
                              <div className="col-span-3"><Label className="text-xs">Pièces</Label><Input data-testid={`od-item-qty-${it._uid}`} className="h-9 mt-1" type="number" value={it.quantity} onChange={(e) => updEdit(it._uid, { quantity: e.target.value })} /></div>
                              <div className="col-span-1 text-right text-xs font-semibold pb-2">{money(editSub(it))}</div>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-slate-500">Total pièces après correction : <b>{editPieces}</b>. Le prix unitaire reste celui du catalogue.</div>
                        <Button data-testid="od-process-btn" onClick={markProcessed} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700">
                          <Cog className="w-4 h-4 mr-2" /> Mettre en traitement
                        </Button>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">Seul le Super Admin peut vérifier les données et mettre la commande en traitement.</p>
                    )}
                  </div>
                )}

                {/* ---- En traitement: marquer prêt (Super Admin) ---- */}
                {st === "En traitement" && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="font-semibold text-slate-700 text-sm">Traitement</div>
                    {isSuperAdmin ? (
                      <>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div><Label className="text-xs">Date de livraison</Label><Input data-testid="od-delivery-date" type="date" value={deliveryDate || ""} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1" /></div>
                          <div><Label className="text-xs">Heure de livraison</Label><Input data-testid="od-delivery-time" type="time" value={deliveryTime || ""} onChange={(e) => setDeliveryTime(e.target.value)} className="mt-1" /></div>
                        </div>
                        <Button data-testid="od-ready-btn" onClick={markReady} disabled={busy} className="bg-purple-600 hover:bg-purple-700">
                          <CheckCircle2 className="w-4 h-4 mr-2" /> Marquer prêt (Traité)
                        </Button>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">Action réservée au Super Admin.</p>
                    )}
                  </div>
                )}

                {/* ---- Nouveau: attribution collecte ---- */}
                {st === "Nouveau" && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="font-semibold text-slate-700 text-sm">Attribution collecte</div>
                    <div>
                      <Label className="text-xs">Livreur</Label>
                      <Select value={courierId} onValueChange={setCourierId}>
                        <SelectTrigger data-testid="od-courier" className="mt-1"><SelectValue placeholder="Choisir un livreur" /></SelectTrigger>
                        <SelectContent>{couriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button data-testid="od-assign-collect-btn" onClick={assign} disabled={busy} variant="outline">
                      <Truck className="w-4 h-4 mr-2" /> Attribuer pour collecte
                    </Button>
                  </div>
                )}

                {/* ---- Traité sans date: planifier la livraison ---- */}
                {st === "Traité" && !scheduled && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="font-semibold text-slate-700 text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Planifier la livraison</div>
                    <p className="text-xs text-slate-500">Convenez de la date et de l'heure avec le client. La commande passera alors dans « Livraisons ».</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Date de livraison</Label><Input data-testid="od-delivery-date" type="date" value={deliveryDate || ""} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1" /></div>
                      <div><Label className="text-xs">Heure de livraison</Label><Input data-testid="od-delivery-time" type="time" value={deliveryTime || ""} onChange={(e) => setDeliveryTime(e.target.value)} className="mt-1" /></div>
                    </div>
                    <Button data-testid="od-plan-delivery-btn" onClick={planDelivery} disabled={busy} className="bg-purple-600 hover:bg-purple-700">
                      <CalendarClock className="w-4 h-4 mr-2" /> Planifier la livraison
                    </Button>
                  </div>
                )}

                {/* ---- Traité planifié: attribution livreur ---- */}
                {st === "Traité" && scheduled && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="font-semibold text-slate-700 text-sm">Attribution livraison</div>
                    <p className="text-xs text-slate-500">Livraison prévue : {fmtDate(order.delivery_date)} à {order.delivery_time}</p>
                    <div>
                      <Label className="text-xs">Livreur</Label>
                      <Select value={courierId} onValueChange={setCourierId}>
                        <SelectTrigger data-testid="od-courier" className="mt-1"><SelectValue placeholder="Choisir un livreur" /></SelectTrigger>
                        <SelectContent>{couriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button data-testid="od-assign-btn" onClick={assign} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700">
                      <Truck className="w-4 h-4 mr-2" /> Attribuer & Dispatcher
                    </Button>
                  </div>
                )}

                {/* ---- Reprogrammation (Super Admin) ---- */}
                {isSuperAdmin && isActive && (
                  <div className="border rounded-xl p-4 space-y-3">
                    <button data-testid="od-reschedule-toggle" onClick={() => setShowReschedule((v) => !v)} className="font-semibold text-slate-700 text-sm flex items-center gap-2">
                      <CalendarClock className="w-4 h-4" /> Reprogrammer collecte / livraison
                    </button>
                    {showReschedule && (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div><Label className="text-xs">Date collecte</Label><Input data-testid="od-re-collect-date" type="date" value={re.collect_date} onChange={(e) => setRe({ ...re, collect_date: e.target.value })} className="mt-1" /></div>
                          <div><Label className="text-xs">Heure collecte</Label><Input data-testid="od-re-collect-time" type="time" value={re.collect_time} onChange={(e) => setRe({ ...re, collect_time: e.target.value })} className="mt-1" /></div>
                          <div><Label className="text-xs">Date livraison</Label><Input data-testid="od-re-delivery-date" type="date" value={re.delivery_date} onChange={(e) => setRe({ ...re, delivery_date: e.target.value })} className="mt-1" /></div>
                          <div><Label className="text-xs">Heure livraison</Label><Input data-testid="od-re-delivery-time" type="time" value={re.delivery_time} onChange={(e) => setRe({ ...re, delivery_time: e.target.value })} className="mt-1" /></div>
                        </div>
                        <Button data-testid="od-reschedule-save" onClick={doReschedule} disabled={busy} variant="outline">
                          <CalendarClock className="w-4 h-4 mr-2" /> Enregistrer la reprogrammation
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* ---- Annulation (uniquement au statut Nouveau) ---- */}
                {st === "Nouveau" && (
                  <div className="border border-rose-200 rounded-xl p-4 space-y-2">
                    <div className="font-semibold text-rose-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" /> Annuler la commande</div>
                    <Textarea data-testid="od-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motif de l'annulation (optionnel)" />
                    <Button data-testid="od-cancel-btn" onClick={() => setConfirmCancel(true)} disabled={busy} className="bg-rose-600 hover:bg-rose-700">
                      <XCircle className="w-4 h-4 mr-2" /> Annuler la commande
                    </Button>
                  </div>
                )}

                {/* ---- Restauration (commande annulée, Super Admin) ---- */}
                {st === "Annulée" && isSuperAdmin && (
                  <div className="border border-emerald-200 rounded-xl p-4 space-y-2">
                    <div className="font-semibold text-emerald-700 text-sm flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Restaurer la commande</div>
                    <p className="text-sm text-slate-500">La commande repassera au statut « Nouveau » et redeviendra opérationnelle.</p>
                    <Button data-testid="od-restore-btn" onClick={restoreOrder} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
                      <RotateCcw className="w-4 h-4 mr-2" /> Restaurer la commande
                    </Button>
                  </div>
                )}

                {order.amount_paid > 0 && (
                  <Button data-testid="od-receipt-btn" onClick={viewReceipt} variant="outline" className="w-full">
                    <ReceiptIcon className="w-4 h-4 mr-2" /> Voir le reçu
                  </Button>
                )}

                <div>
                  <div className="flex items-center gap-2 font-semibold text-slate-700 mb-2 text-sm">
                    <History className="w-4 h-4" /> Historique des statuts
                  </div>
                  <div className="space-y-2">
                    {(order.history || []).map((h) => (
                      <div key={h.id} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-brand-500 mt-1.5" />
                        <span className="text-slate-500 whitespace-nowrap">{fmtDateTime(h.at)}</span>
                        <span>{h.old_status && h.old_status !== h.new_status ? `${h.old_status} → ` : ""}<b>{h.new_status}</b>{h.comment ? <span className="text-slate-400"> · {h.comment}</span> : ""}</span>
                        <span className="text-slate-400 whitespace-nowrap">· {h.user_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {["super_admin", "logistics_manager"].includes(user?.role) && (
                  <OrderAuditLog orderId={order.id} refreshKey={order} />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><XCircle className="w-5 h-5 text-rose-600" /> Confirmer l'annulation</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Êtes-vous sûr de vouloir annuler la commande <b className="font-mono">{order?.order_number}</b> de <b>{order?.client_name}</b> ?</p>
          {reason && <p className="text-xs text-slate-500">Motif : {reason}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmCancel(false)}>Non, garder</Button>
            <Button data-testid="od-cancel-confirm" onClick={() => { setConfirmCancel(false); cancelOrder(); }} disabled={busy} className="bg-rose-600 hover:bg-rose-700">Oui, annuler</Button>
          </div>
        </DialogContent>
      </Dialog>
      {receipt && <Receipt receipt={receipt} onClose={() => setReceipt(null)} />}
    </>
  );
}
